import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Loader2 } from 'lucide-react';

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const ReservationSettingsDialog = ({ open, onOpenChange, settings, onSave, saving }) => {
  const [form, setForm] = useState(() => JSON.parse(JSON.stringify(settings)));
  const setTier = (i, k, v) => setForm(f => ({ ...f, tiers: f.tiers.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));

  const submit = () => onSave({
    ...form,
    public_opens_at: form.public_opens_at || '',
    hold_hours: Number(form.hold_hours),
    tiers: form.tiers.map(t => ({ ...t, price: Number(t.price), capacity: Number(t.capacity) })),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="reservation-settings-dialog">
        <DialogHeader><DialogTitle>Reservation Settings · {form.season}</DialogTitle></DialogHeader>
        <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-1">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium">Reservations open</p>
              <p className="text-xs text-gray-500">Turn off to hide the tier cards and stop new holds</p>
            </div>
            <Switch checked={form.open} onCheckedChange={(v) => setForm({ ...form, open: v })} data-testid="settings-open-switch" />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><Label>Season</Label><Input value={form.season} onChange={e => setForm({ ...form, season: e.target.value })} data-testid="settings-season" /></div>
            <div><Label>Hold (hours)</Label><Input type="number" min={1} value={form.hold_hours} onChange={e => setForm({ ...form, hold_hours: e.target.value })} data-testid="settings-hold-hours" /></div>
            <div><Label>Venmo username</Label><Input placeholder="e.g. ROJO-Retreat" value={form.venmo_username || ''} onChange={e => setForm({ ...form, venmo_username: e.target.value })} data-testid="settings-venmo-username" /></div>
          </div>
          <p className="text-xs text-gray-500 -mt-3">With a Venmo username set, the pay link pre-fills the amount and reference code. Without it, we fall back to your Venmo QR link below.</p>
          <div>
            <Label>Public opening date &amp; time (alumni-only before this)</Label>
            <div className="flex gap-2 mt-1">
              <Input type="datetime-local" value={toLocalInput(form.public_opens_at)} onChange={e => setForm({ ...form, public_opens_at: e.target.value ? new Date(e.target.value).toISOString() : '' })} data-testid="settings-public-opens-at" />
              {form.public_opens_at && <Button type="button" variant="outline" onClick={() => setForm({ ...form, public_opens_at: '' })} data-testid="settings-clear-opens-at">Clear</Button>}
            </div>
            <p className="text-xs text-gray-500 mt-1">Until this moment only {form.archive_season || '2026'} attendees (matched by email) can reserve. Leave empty to let everyone in.</p>
          </div>
          <div><Label>Fallback Venmo link</Label><Input value={form.venmo_link || ''} onChange={e => setForm({ ...form, venmo_link: e.target.value })} data-testid="settings-venmo-link" /></div>

          <div>
            <p className="text-sm font-semibold mb-2">Tiers</p>
            <div className="space-y-3">
              {form.tiers.map((t, i) => (
                <div key={t.id} className="rounded-lg border p-3 grid sm:grid-cols-[1fr,90px,90px] gap-3" data-testid={`settings-tier-${t.id}`}>
                  <div className="space-y-2">
                    <Input value={t.name} onChange={e => setTier(i, 'name', e.target.value)} placeholder="Tier name" data-testid={`tier-name-${t.id}`} />
                    <Input value={t.tagline || ''} onChange={e => setTier(i, 'tagline', e.target.value)} placeholder="Short tagline" className="text-xs" />
                    <Input value={t.description || ''} onChange={e => setTier(i, 'description', e.target.value)} placeholder="Description shown on the card" className="text-xs" />
                  </div>
                  <div><Label className="text-xs">Price $</Label><Input type="number" min={0} value={t.price} onChange={e => setTier(i, 'price', e.target.value)} data-testid={`tier-price-${t.id}`} /></div>
                  <div><Label className="text-xs">Beds</Label><Input type="number" min={0} value={t.capacity} onChange={e => setTier(i, 'capacity', e.target.value)} data-testid={`tier-capacity-input-${t.id}`} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-gradient-to-r from-wine to-burgundy text-white" data-testid="settings-save-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
